import { useCallback, useEffect, useRef, useState } from "react";
import type { Message, Ticket } from "posthog-js";
import {
  captureException,
  getSupportConversations,
  getVerifiedAnalyticsDistinctId,
  subscribeToVerifiedAnalyticsIdentity,
  trackEvent,
} from "../integrations/analytics/client";
import { supabase } from "../integrations/supabase/client";

/** PostHog only reports conversations as available once remote config has landed. */
const AVAILABILITY_POLL_MS = 500;
const AVAILABILITY_TIMEOUT_MS = 10000;
/** Agent replies arrive server-side, so an open panel has to ask for them. */
const MESSAGE_POLL_MS = 5000;
/**
 * While the panel is closed we only need to know *whether* a reply landed, to badge the
 * sidebar — a much slower cadence than the open transcript, and it only runs at all once the
 * user has an existing ticket, so someone who never contacts support makes no requests.
 */
const UNREAD_POLL_MS = 60000;
const TICKET_PAGE_SIZE = 20;

export type SupportChatStatus = "checking" | "ready" | "unavailable";

/** `null` selection means "composing a brand new conversation". */
export type SupportChatSelection = string | null;

const toDisplayName = (metadata: Record<string, unknown> | null | undefined) => {
  if (!metadata) return undefined;

  const first = typeof metadata.first_name === "string" ? metadata.first_name.trim() : "";
  const last = typeof metadata.last_name === "string" ? metadata.last_name.trim() : "";
  const fromParts = `${first} ${last}`.trim();
  const full = typeof metadata.full_name === "string" ? metadata.full_name.trim() : "";

  return full || fromParts || undefined;
};

const sumUnread = (tickets: Ticket[]) =>
  tickets.reduce((total, ticket) => total + (ticket.unread_count ?? 0), 0);

/**
 * Drives the in-app support chat against `posthog.conversations`, which is the same backend
 * as PostHog's own widget without its UI. Availability is remote-config gated (Support has
 * to be enabled for the project), so every consumer has to handle "unavailable" — the Help
 * page keeps its mailto fallback for exactly that case.
 *
 * Multiple conversations are supported for reading, with one constraint imposed by the SDK:
 * `sendMessage` only ever targets PostHog's own "current" ticket (or creates a new one) —
 * there is no `sendMessage(ticketId, …)`. So an older ticket that isn't the current one is
 * readable but not replyable, and the UI offers a new conversation instead of silently
 * posting the reply onto the wrong thread. See `canReplyToSelection`.
 */
export const useSupportChat = (isOpen: boolean) => {
  const [status, setStatus] = useState<SupportChatStatus>("checking");
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<SupportChatSelection>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  /**
   * Unread count for the open thread as it was *before* we marked it read, so the transcript
   * can scroll to the first unread reply rather than blindly to the bottom.
   */
  const [unreadAnchorCount, setUnreadAnchorCount] = useState(0);
  const hasTrackedOpenRef = useRef(false);
  const openedAtRef = useRef<number | null>(null);
  const hasEverHadTicketRef = useRef(false);
  /**
   * PostHog resolves tickets against *either* the verified identity or the anonymous widget
   * session, never both. Verification depends on the session, so it lands after mount — and
   * anything loaded before it belongs to the scope we're no longer querying. Tracking it here
   * lets the loaders below re-run against the scope actually in force.
   */
  const [verifiedIdentity, setVerifiedIdentity] = useState<string | null>(
    getVerifiedAnalyticsDistinctId
  );

  useEffect(
    () =>
      subscribeToVerifiedAnalyticsIdentity(() => {
        setVerifiedIdentity(getVerifiedAnalyticsDistinctId());
      }),
    []
  );

  // Drop the other scope's tickets rather than leaving a thread on screen that the new scope
  // can't fetch messages for (or, worse, selected when a reply gets sent).
  useEffect(() => {
    setTickets([]);
    setMessages([]);
    setSelectedTicketId(null);
    setUnreadCount(0);
    setUnreadAnchorCount(0);
    hasEverHadTicketRef.current = false;
  }, [verifiedIdentity]);

  useEffect(() => {
    const conversations = getSupportConversations();

    if (!conversations) {
      setStatus("unavailable");
      return;
    }

    let cancelled = false;
    let elapsed = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const check = () => {
      if (cancelled) return;

      if (conversations.isAvailable()) {
        // We render the transcript ourselves; PostHog's own launcher would be a second,
        // conflicting entry point into the same tickets.
        conversations.hide();
        setStatus("ready");
        return;
      }

      elapsed += AVAILABILITY_POLL_MS;
      if (elapsed >= AVAILABILITY_TIMEOUT_MS) {
        setStatus("unavailable");
        return;
      }

      timer = setTimeout(check, AVAILABILITY_POLL_MS);
    };

    check();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const refreshTickets = useCallback(async ({ silent }: { silent: boolean }) => {
    const conversations = getSupportConversations();
    if (!conversations?.isAvailable()) return [] as Ticket[];

    if (!silent) setIsLoadingTickets(true);

    try {
      const response = await conversations.getTickets({ limit: TICKET_PAGE_SIZE });
      if (!response) return [] as Ticket[];

      setTickets(response.results);
      setUnreadCount(sumUnread(response.results));
      if (response.results.length > 0) {
        hasEverHadTicketRef.current = true;
      }

      return response.results;
    } catch (error) {
      captureException(error, { source: "support_chat", phase: "get_tickets" });
      return [] as Ticket[];
    } finally {
      if (!silent) setIsLoadingTickets(false);
    }
  }, []);

  const refreshMessages = useCallback(
    async (ticketId: string, { silent }: { silent: boolean }) => {
      const conversations = getSupportConversations();
      if (!conversations?.isAvailable()) return;

      if (!silent) setIsLoadingMessages(true);

      try {
        const response = await conversations.getMessages(ticketId);
        if (response) {
          // Internal notes are agent-to-agent and must never render to the customer.
          setMessages(response.messages.filter((message) => !message.is_private));
        }
      } catch (error) {
        captureException(error, { source: "support_chat", phase: "get_messages" });
      } finally {
        if (!silent) setIsLoadingMessages(false);
      }
    },
    []
  );

  // On open, load the conversation list and land the user in the most recent thread — the
  // common case is one ongoing conversation, and making that a two-tap journey would be worse
  // than the list being one tap away.
  useEffect(() => {
    if (!isOpen || status !== "ready") return;

    let cancelled = false;

    void (async () => {
      const loaded = await refreshTickets({ silent: false });
      if (cancelled || loaded.length === 0) return;

      setSelectedTicketId((current) => current ?? loaded[0].id);
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, status, refreshTickets, verifiedIdentity]);

  // Poll the open thread. A closed panel polls the much cheaper ticket list instead (below).
  useEffect(() => {
    if (!isOpen || status !== "ready" || !selectedTicketId) {
      if (!selectedTicketId) setMessages([]);
      return;
    }

    let cancelled = false;

    void refreshMessages(selectedTicketId, { silent: false });

    const interval = setInterval(() => {
      if (cancelled) return;
      void refreshMessages(selectedTicketId, { silent: true });
      void refreshTickets({ silent: true });
    }, MESSAGE_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isOpen, status, selectedTicketId, refreshMessages, refreshTickets]);

  // Keeps the sidebar badge live for a user who wrote in and then closed the panel.
  useEffect(() => {
    if (isOpen || status !== "ready") return;

    const conversations = getSupportConversations();
    if (!conversations) return;
    if (!hasEverHadTicketRef.current && !conversations.getCurrentTicketId()) return;

    let cancelled = false;

    void refreshTickets({ silent: true });

    const interval = setInterval(() => {
      if (!cancelled) void refreshTickets({ silent: true });
    }, UNREAD_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isOpen, status, refreshTickets, verifiedIdentity]);

  // Viewing a thread is the user seeing the team's replies. Snapshot the unread count first
  // so the transcript can still scroll to where they left off.
  useEffect(() => {
    if (!isOpen || status !== "ready" || !selectedTicketId) {
      setUnreadAnchorCount(0);
      return;
    }

    const conversations = getSupportConversations();
    if (!conversations) return;

    const ticket = tickets.find((entry) => entry.id === selectedTicketId);
    const ticketUnread = ticket?.unread_count ?? 0;
    if (ticketUnread === 0) return;

    setUnreadAnchorCount(ticketUnread);

    void conversations
      .markAsRead(selectedTicketId)
      .then(() => {
        setTickets((previous) => {
          const next = previous.map((entry) =>
            entry.id === selectedTicketId ? { ...entry, unread_count: 0 } : entry
          );
          setUnreadCount(sumUnread(next));
          return next;
        });
      })
      .catch((error: unknown) => {
        captureException(error, { source: "support_chat", phase: "mark_as_read" });
      });
  }, [isOpen, status, selectedTicketId, tickets]);

  // Session shape at close time, read through refs so the open/close effect below doesn't
  // re-run (and re-fire) every time a poll updates the ticket list.
  const sessionShapeRef = useRef({ ticketCount: 0, unreadCount: 0, messageCount: 0 });
  sessionShapeRef.current = {
    ticketCount: tickets.length,
    unreadCount,
    messageCount: messages.length,
  };

  useEffect(() => {
    if (!isOpen) {
      if (hasTrackedOpenRef.current) {
        const openedAt = openedAtRef.current;
        trackEvent("support_chat_closed", {
          status,
          durationMs: openedAt === null ? null : Date.now() - openedAt,
          ...sessionShapeRef.current,
        });
      }

      hasTrackedOpenRef.current = false;
      openedAtRef.current = null;
      return;
    }

    if (hasTrackedOpenRef.current || status === "checking") return;

    hasTrackedOpenRef.current = true;
    openedAtRef.current = Date.now();
    trackEvent("support_chat_opened", { status });
  }, [isOpen, status]);

  const selectTicket = useCallback((ticketId: SupportChatSelection) => {
    setSelectedTicketId(ticketId);
    setSendError(null);
    setUnreadAnchorCount(0);
    if (!ticketId) setMessages([]);
  }, []);

  const startNewConversation = useCallback(() => {
    selectTicket(null);
    trackEvent("support_chat_conversation_started", { existingCount: tickets.length });
  }, [selectTicket, tickets.length]);

  /**
   * False for an older thread that PostHog no longer considers current — the SDK would post
   * the reply to the current ticket instead of this one, so the UI has to offer a new
   * conversation rather than send it to the wrong place.
   */
  const canReplyToSelection = (() => {
    if (selectedTicketId === null) return true;

    const conversations = getSupportConversations();
    const currentTicketId = conversations?.getCurrentTicketId() ?? null;

    // Before any ticket is current (fresh session), the newest thread is the one a reply
    // would land on anyway.
    if (!currentTicketId) return selectedTicketId === tickets[0]?.id;

    return selectedTicketId === currentTicketId;
  })();

  const sendMessage = useCallback(
    async (text: string) => {
      const body = text.trim();
      const conversations = getSupportConversations();

      if (!body || !conversations?.isAvailable()) return false;

      const isNewConversation = selectedTicketId === null;

      setIsSending(true);
      setSendError(null);

      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        const response = await conversations.sendMessage(
          body,
          {
            email: user?.email ?? undefined,
            name: toDisplayName(user?.user_metadata),
          },
          isNewConversation
        );

        if (!response) {
          setSendError("Couldn't send that message. Please try again.");
          return false;
        }

        trackEvent("support_chat_message_sent", {
          isNewConversation,
          isFirstMessage: messages.length === 0,
          ticketStatus: response.ticket_status,
        });

        // Follow the ticket the message actually landed on rather than assuming — for a new
        // conversation that's the id we only learn from the response.
        setSelectedTicketId(response.ticket_id);
        await Promise.all([
          refreshMessages(response.ticket_id, { silent: true }),
          refreshTickets({ silent: true }),
        ]);
        return true;
      } catch (error) {
        captureException(error, { source: "support_chat", phase: "send_message" });
        setSendError("Couldn't send that message. Please try again.");
        return false;
      } finally {
        setIsSending(false);
      }
    },
    [messages.length, refreshMessages, refreshTickets, selectedTicketId]
  );

  return {
    status,
    tickets,
    selectedTicketId,
    selectTicket,
    startNewConversation,
    canReplyToSelection,
    messages,
    isLoadingMessages,
    isLoadingTickets,
    isSending,
    sendError,
    sendMessage,
    unreadCount,
    unreadAnchorCount,
  };
};

export type SupportChatState = ReturnType<typeof useSupportChat>;
